/**
 * Immer-safe deep merge utilities.
 *
 * Lodash `merge` / `mergeWith` internally inspects `.constructor.prototype`
 * via `isPrototype` and `initCloneObject`.  When the target is an Immer Draft
 * (a Proxy whose `prototype` property is non-configurable on the underlying
 * target), the Proxy invariant check throws:
 *
 *   TypeError: 'get' on proxy: property 'prototype' is a read-only and
 *   non-configurable data property on the proxy target but the proxy did not
 *   return its actual value
 *
 * These helpers perform a recursive property-level merge that never reads
 * `.constructor` or `.prototype`, so they work safely on Immer drafts.
 */

type Customizer = (objValue: unknown, srcValue: unknown, key: string) => unknown

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false
  if (Array.isArray(value)) return false
  // Avoid inspecting prototype chain – just check it's a simple object
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * Recursively merge `src` into `target` (mutates `target`).
 * Equivalent to `lodash.merge(target, src)`.
 */
export function draftMerge<T extends object>(
  target: T,
  src: Partial<T> | Record<string, unknown>,
): T {
  if (!src) return target

  const keys = Object.keys(src)
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const srcVal = (src as Record<string, unknown>)[key]
    const tgtVal = (target as Record<string, unknown>)[key]

    if (srcVal === undefined) {
      continue
    }

    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      draftMerge(
        tgtVal as Record<string, unknown>,
        srcVal as Record<string, unknown>,
      )
    } else {
      ;(target as Record<string, unknown>)[key] = srcVal
    }
  }

  return target
}

/**
 * Recursively merge `src` into `target` with a customizer (mutates `target`).
 * Equivalent to `lodash.mergeWith(target, src, customizer)`.
 *
 * When the customizer returns `undefined`, the default deep-merge logic is
 * used (same behaviour as lodash).
 */
export function draftMergeWith<T extends object>(
  target: T,
  src: Partial<T> | Record<string, unknown>,
  customizer: Customizer,
): T {
  if (!src) return target

  const keys = Object.keys(src)
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const srcVal = (src as Record<string, unknown>)[key]
    const tgtVal = (target as Record<string, unknown>)[key]

    const customResult = customizer(tgtVal, srcVal, key)
    if (customResult !== undefined) {
      ;(target as Record<string, unknown>)[key] = customResult
      continue
    }

    if (srcVal === undefined) {
      continue
    }

    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      draftMergeWith(
        tgtVal as Record<string, unknown>,
        srcVal as Record<string, unknown>,
        customizer,
      )
    } else {
      ;(target as Record<string, unknown>)[key] = srcVal
    }
  }

  return target
}
